import { useEffect, useState } from "react"

import { BookOpen, ChevronDown, Globe, Lock, Scale } from "lucide-react"

import { getFirstName, getInitials } from "@lib/user"

import { useUserSession } from "@session/UserSession"

import { Avatar, AvatarFallback } from "@ui/avatar"
import { Button } from "@ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@ui/popover"
import { Separator } from "@ui/separator"

export function UserButton() {
    const { authToken, logout, client } = useUserSession()
    const [open, setOpen] = useState(false)
    const [pictureUrl, setPictureUrl] = useState<string | null>(null)

    const handleNavigate = (path: string) => {
        window.open(path, "_blank")
    }

    const handleLogout = () => {
        logout()
    }

    useEffect(() => {
        if (!client || !authToken || authToken?.sub.includes(":")) {
            setPictureUrl(null)
            return
        }

        client.users.retrieve(authToken.sub).then((response) => {
            setPictureUrl(response?.picture ? response.picture : null)
        })
    }, [authToken, client])

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <div className="flex flex-row border items-center justify-center cursor-pointer hover:bg-gray-200 rounded-md">
                    <div className="px-2 text-sm font-light text-left">{getFirstName(authToken?.name)}</div>
                    <div className="border-l">
                        <ChevronDown className={`p-1 opacity-50 transition-transform duration-300 ${open ? "rotate-180" : "rotate-0"}`} />
                    </div>
                </div>
            </PopoverTrigger>
            <PopoverContent className="w-[350px] mb-2 mr-4 text-primary-dark p-4 border shadow" align="start">
                <div className="flex flex-col space-y-2">
                    <div className="flex items-start space-x-2">
                        <Avatar className="h-12 w-12 bg-primary-dark">
                            <AvatarFallback className="text-white text-xl hover:opacity-75">{pictureUrl ? <img src={pictureUrl} alt="User Avatar" /> : getInitials(authToken?.name)}</AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col">
                            <span className="font-bold">{authToken?.name}</span>
                            <span className="text-sm italic">{authToken?.email}</span>
                        </div>
                    </div>
                    <Separator className="bg-gray-300" />
                    <div className="flex items-center justify-between">
                        <span>Documentation</span>
                        <Button onClick={() => handleNavigate("https://docs.vertesiahq.com")} size="icon" variant="ghost">
                            <BookOpen />
                        </Button>
                    </div>
                    <div className="flex items-center justify-between">
                        <span>Privacy</span>
                        <Button onClick={() => handleNavigate("https://vertesiahq.com/privacy")} size="icon" variant="ghost">
                            <Lock />
                        </Button>
                    </div>
                    <div className="flex items-center justify-between">
                        <span>Terms of Service</span>
                        <Button onClick={() => handleNavigate("https://vertesiahq.com/terms")} size="icon" variant="ghost">
                            <Scale />
                        </Button>
                    </div>
                    <div className="flex items-center justify-between">
                        <span>Website</span>
                        <Button onClick={() => handleNavigate("https://vertesiahq.com")} size="icon" variant="ghost">
                            <Globe />
                        </Button>
                    </div>
                    <Separator className="bg-gray-300" />
                    <Button variant="outline" onClick={handleLogout}>
                        Sign Out
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    )
}
